import React from 'react';
import styled, { css } from 'styled-components';

import { TableHeaderDefinitionCellType } from '~/components/DataTables/types.flow';

import TEST_ID from './flowListColumns.testid';
import TableHeaderCell from '~/components/DataTables/util/TableHeaderCell';
import StatisticsProgressBar from '~/components/StatisticsProgressBar';
import {
  FlowV2_ConditionListFragment,
  Flow_Statistics,
  SortDirection,
} from '~/graphql/types';
import { formattedDateWithMonth, isToday } from '~/util/date';
import BuilderContext, {
  IBuilderContext,
} from '../../v2/components/Builder/context';
import RepresentationString from '../../v2/components/RepresentationString';
import JustificationContainer from '~/components/JustificationContainer';

export type SortToggleFunction = (
  sortField: string,
  sortDirection: SortDirection | null,
) => void;

const text = {
  notModifiedMessage: 'Nooit',
};

const StyledRepresentationString = styled(RepresentationString)`
  font-weight: normal;
`;

const TriggerComp: React.FC<{
  iBuilderContext: IBuilderContext;
  actionId: string;
  conditionList?: FlowV2_ConditionListFragment;
}> = ({ iBuilderContext, conditionList, actionId }) => {
  if (!conditionList) return <>Geen condities</>;

  if (!conditionList.conditions[0]) return <>Geen condities</>;

  return (
    <BuilderContext.Provider value={iBuilderContext}>
      <JustificationContainer direction="column">
        <StyledRepresentationString
          key={conditionList.conditions[0].id}
          condition={conditionList.conditions[0]}
          actionId={actionId}
          hasIssue={false}
        />
      </JustificationContainer>
    </BuilderContext.Provider>
  );
};

const flowListColumns = (
  sortToggle: SortToggleFunction,
  version: string,
  isV2?: boolean,
): Array<TableHeaderDefinitionCellType<any>> => {
  const triggerColumn: TableHeaderDefinitionCellType<any> =
    TableHeaderCell.custom({
      label: 'Trigger',
      dataProperty: 'trigger',
      width: 200,
      dataTestid: TEST_ID.TRIGGER,
      sorting: {
        onSort: sortDirection => sortToggle('trigger', sortDirection),
      },
      version,
      renderFunction: value => <TriggerComp {...value} />,
    });

  const columns = [
    TableHeaderCell.string({
      label: 'Naam',
      dataProperty: 'name',
      width: 250,
      dataTestid: TEST_ID.NAME,
      sorting: {
        onSort: sortDirection => sortToggle('name', sortDirection),
      },
    }),
    /** This is only added to access flow description when copying flow-v2 */
    TableHeaderCell.string({
      label: 'Description',
      dataProperty: 'description',
      sorting: {
        onSort: sortDirection => sortToggle('description', sortDirection),
      },
      isResizable: false,
      width: 0,
      dataTestid: TEST_ID.DESCRIPTION,
    }),
    TableHeaderCell.custom<Flow_Statistics>({
      label: 'Resultaten',
      dataProperty: 'Statistics',
      dataTestid: TEST_ID.STATISTICS,
      width: 160,
      renderFunction: value => (
        <StatisticsProgressBar
          completedAmount={value.completedFlowInstancesTotal}
          nonCompletedAmount={value.nonCompletedFlowInstancesTotal}
        />
      ),
      sorting: {
        onSort: sortDirection => sortToggle('Statistics', sortDirection),
      },
      version,
    }),
    TableHeaderCell.date({
      label: 'Aangemaakt',
      dataProperty: 'createdDate',
      dataTestid: TEST_ID.CREATED,
      width: 90,
      renderFunction: value => (
        <CreatedDateLabel isToday={isToday(value)}>
          {formattedDateWithMonth(value, {
            showTodayAsLabel: true,
            showYesterdayAsLabel: true,
          })}
        </CreatedDateLabel>
      ),
      sorting: {
        onSort: sortDirection => sortToggle('createdDate', sortDirection),
      },
    }),
    TableHeaderCell.date({
      label: 'Laatst gewijzigd',
      dataProperty: 'lastModified',
      dataTestid: TEST_ID.LAST_MODIFIED,
      width: 110,
      sorting: {
        onSort: sortDirection => sortToggle('lastModified', sortDirection),
      },
      renderFunction: value => {
        const lastModifiedString = value
          ? formattedDateWithMonth(value, {
              showTodayAsLabel: true,
              showYesterdayAsLabel: true,
            })
          : text.notModifiedMessage;

        return <span>{lastModifiedString}</span>;
      },
    }),
    TableHeaderCell.boolean({
      label: 'Status',
      dataProperty: 'enabled',
      width: 75,
      sorting: {
        onSort: sortDirection => sortToggle('enabled', sortDirection),
      },
      renderFunction: value => (
        <StatusContainer>
          <Indicator active={value} />
          <Label>{value ? 'Actief' : 'Inactief'}</Label>
        </StatusContainer>
      ),
      dataTestid: TEST_ID.ENABLED,
    }),
    TableHeaderCell.action({
      dataTestid: TEST_ID.ACTION,
    }),
  ];

  if (isV2 === true) {
    columns.splice(1, 0, triggerColumn);
  }

  return columns;
};

const StatusContainer = styled.div<{}>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;
type IndicatorProps = {
  active: boolean;
};
const Indicator = styled.div<IndicatorProps>`
  border-radius: 50%;
  display: inline-block;

  ${({ theme, active }) => css`
    background-color: ${active ? theme.color('success') : theme.color('grey')};
    width: ${theme.space('m')};
    min-width: ${theme.space('m')};
    height: ${theme.space('m')};
    margin-right: ${theme.space('xxs')};
  `}
`;

const Label = styled.div<{}>`
  display: inline-block;
`;

type CreatedDateLabelProps = {
  isToday: boolean;
};
const CreatedDateLabel = styled.span<CreatedDateLabelProps>(
  ({ theme, isToday }) => {
    if (isToday) {
      return css`
        color: ${theme.color('success')};
      `;
    }
    return null;
  },
);

export default flowListColumns;
